import { type ClipboardEvent, type KeyboardEvent, useEffect, useRef, useState } from "react";

export type PinInputProps = {
  length?: number;
  value?: string;
  onChange?: (value: string) => void;
  onComplete?: (value: string) => void;
  mask?: boolean;
  disabled?: boolean;
  error?: string;
  autoFocus?: boolean;
  className?: string;
};

export function PinInput({
  length = 6,
  value,
  onChange,
  onComplete,
  mask = false,
  disabled = false,
  error,
  autoFocus = false,
  className = "",
}: PinInputProps) {
  const [internalValue, setInternalValue] = useState("");
  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const updateValue = (newValue: string) => {
    const sanitized = newValue.replace(/\D/g, "").slice(0, length);
    if (!isControlled) {
      setInternalValue(sanitized);
    }
    onChange?.(sanitized);

    if (sanitized.length === length) {
      onComplete?.(sanitized);
    }
  };

  const focusInput = (index: number) => {
    const input = inputRefs.current[index];
    if (input) {
      input.focus();
      input.select();
    }
  };

  const handleChange = (index: number, char: string) => {
    if (!/^\d*$/.test(char)) return;

    const chars = currentValue.split("");
    chars[index] = char;
    const newValue = chars.join("").slice(0, length);
    updateValue(newValue);

    // Move to next input
    if (char && index < length - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const chars = currentValue.split("");

      if (chars[index]) {
        // Clear current
        chars[index] = "";
        updateValue(chars.join(""));
      } else if (index > 0) {
        // Move to previous and clear
        chars[index - 1] = "";
        updateValue(chars.join(""));
        focusInput(index - 1);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      focusInput(index - 1);
    } else if (e.key === "ArrowRight" && index < length - 1) {
      e.preventDefault();
      focusInput(index + 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    updateValue(pasted);

    // Focus last filled or next empty
    const focusIndex = Math.min(pasted.length, length - 1);
    focusInput(focusIndex);
  };

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <div className="flex justify-center gap-2">
        {Array.from({ length }).map((_, index) => {
          const char = currentValue[index] || "";
          const isFilled = char !== "";

          return (
            <input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type={mask ? "password" : "text"}
              inputMode="numeric"
              maxLength={1}
              value={char}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              onFocus={(e) => e.target.select()}
              disabled={disabled}
              className={`
                w-12 h-14 text-center text-xl font-bold rounded-lg
                bg-surface-800 border transition-all duration-200
                focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent
                disabled:opacity-50 disabled:cursor-not-allowed
                ${
                  error
                    ? "border-error-500"
                    : isFilled
                      ? "border-brand-500"
                      : "border-surface-700 hover:border-surface-600"
                }
                ${mask ? "text-brand-500" : "text-white"}
              `}
              aria-label={`Pin digit ${index + 1}`}
            />
          );
        })}
      </div>
      {error && <p className="text-sm text-error-500">{error}</p>}
    </div>
  );
}
